import { useNavigate } from "react-router";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ROUTES } from "@/app/routes";
import { usePostApiIdentityRules } from "@/lib/api/generated/identity/identity";
import { postApiIdentityUsers } from "@/lib/api/generated/identity/identity";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { queryKeys } from "@/lib/query/keys";
import { toastMutationError } from "@/lib/utils/formatting";
import type { User } from "@/lib/api/generated/control-api.schemas";

const identityRuleChannels = ["webchat", "whatsapp", "slack", "msteams", "other"] as const;

const CHANNEL_LABELS: Record<(typeof identityRuleChannels)[number], string> = {
  webchat: "Web Chat",
  whatsapp: "WhatsApp",
  slack: "Slack",
  msteams: "Microsoft Teams",
  other: "Other",
};

const createUserSchema = z.object({
  channel: z.enum(identityRuleChannels),
  userId: z.string().min(1, "User is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().min(1, "Email is required").email("Must be a valid email address"),
  phone: z.string().optional(),
});

type CreateUserForm = z.infer<typeof createUserSchema>;

export function CreateIdentityRulePage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const mutation = usePostApiIdentityRules();
  const { data: directoryUsers, error: directoryUsersError } = useQuery({
    queryKey: queryKeys.tenantDirectoryUsers(tenantId ?? ""),
    queryFn: async () =>
      postApiIdentityUsers({
        tenantId: tenantId ?? "",
        page: 1,
        pageSize: 100,
      }),
    enabled: Boolean(tenantId),
  });
  useDocumentTitle("Add Identity Mapping");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserForm>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      channel: "webchat",
      userId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  const userIdValue = useWatch({ control, name: "userId" });
  const selectedUser = (directoryUsers?.items ?? []).find(
    (user) => user.id === userIdValue,
  );

  const onSubmit = async (data: CreateUserForm) => {
    const displayName = `${data.firstName} ${data.lastName}`;

    try {
      await mutation.mutateAsync({
        data: {
          tenantId: tenantId!,
          channel: data.channel,
          userId: data.userId,
          displayName,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone?.trim() || null,
        },
      });
      toast.success("Identity mapping created");
      navigate(ROUTES.tenantIdentity(tenantId!));
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to create identity mapping"));
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Add Identity Mapping
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>Mapping Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField label="User" error={errors.userId?.message}>
              <select {...register("userId")} className="form-input">
                <option value="">Select user</option>
                {(directoryUsers?.items ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {formatUserOption(user)}
                  </option>
                ))}
              </select>
              {selectedUser ? (
                <span className="text-[11px] mt-1" style={{ color: "var(--muted-foreground)" }}>
                  {selectedUser.displayName} is the assigned user for this mapping.
                </span>
              ) : directoryUsersError instanceof Error ? (
                <span className="text-[11px] mt-1" style={{ color: "var(--danger)" }}>
                  {directoryUsersError.message}
                </span>
              ) : null}
            </FormField>

            <FormField label="Channel" error={errors.channel?.message}>
              <select {...register("channel")} className="form-input">
                {identityRuleChannels.map((channel) => (
                  <option key={channel} value={channel}>
                    {CHANNEL_LABELS[channel]}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="First Name" error={errors.firstName?.message}>
              <input
                {...register("firstName")}
                placeholder="Alice"
                className="form-input"
              />
            </FormField>

            <FormField label="Last Name" error={errors.lastName?.message}>
              <input
                {...register("lastName")}
                placeholder="Example"
                className="form-input"
              />
            </FormField>

            <FormField label="Email" error={errors.email?.message}>
              <input
                {...register("email")}
                type="email"
                placeholder="alice@example.com"
                className="form-input"
              />
            </FormField>

            <FormField label="Phone" error={errors.phone?.message}>
              <input
                {...register("phone")}
                placeholder="+15550001234"
                className="form-input"
              />
            </FormField>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Mapping"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate(ROUTES.tenantIdentity(tenantId!))}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function formatUserOption(user: User): string {
  const parts = [user.displayName];
  if (user.email) {
    parts.push(user.email);
  }
  parts.push(user.id);
  return parts.join(" · ");
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error && (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      )}
    </div>
  );
}
